import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Science() {
  return (
    <>
      {/* Hero */}
      <section className="page-hero">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatScience} />
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Evidence-Based</span>
            <h1>Science Behind the Transformation</h1>
            <p>
              Understanding why carnivore eating and strategic fasting create such powerful results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="science-section">
              <h3>Ketosis & Metabolic Flexibility</h3>
              <p>
                When you eliminate carbohydrates and eat primarily animal foods, your body shifts from
                burning glucose to burning fat. This metabolic state—ketosis—has profound effects on
                energy and mental clarity.
              </p>
              <p>
                But the real magic happens when your body becomes metabolically flexible: able to
                seamlessly switch between fuel sources. This is how humans evolved to function, and
                it's what makes fasting so much easier on a carnivore diet.
              </p>
            </div>

            <div className="science-section">
              <h3>Elimination Diet Benefits</h3>
              <p>
                The carnivore diet is essentially an elimination diet—removing all plant compounds
                at once. Many plant foods contain compounds like lectins, oxalates, and phytates
                that some people find they do better without.
              </p>
              <p>
                By simplifying what you eat to just animal foods, you can then see how your body
                responds. Many people report enjoying the simplicity and how they feel on this approach.
              </p>
            </div>

            <div className="science-section">
              <h3>Autophagy & Cellular Renewal</h3>
              <p>
                Autophagy—literally "self-eating"—is your body's built-in recycling system. During
                fasting, your cells break down old components and rebuild them into new structures.
                This is cellular renewal at its most fundamental level.
              </p>
              <p>
                Fasting is known to increase autophagy. This is one of the reasons many people
                find value in incorporating fasting into their routine.
              </p>
            </div>

            <div className="science-section">
              <h3>Brain Function & Ketones</h3>
              <p>
                Your brain can run on two fuels: glucose and ketones. When you're in ketosis, your
                brain has access to ketones as a fuel source. Many people who try carnivore report
                enjoying the mental clarity they experience.
              </p>
              <p>
                The simplicity of carnivore eating—knowing exactly what you're going to eat—also
                removes decision fatigue that many people find mentally draining.
              </p>
            </div>

            <div className="science-section">
              <h3>Saturated Fat Reconsidered</h3>
              <p>
                The relationship between saturated fat and cardiovascular outcomes is more nuanced
                than previously thought. Recent research has challenged some long-held assumptions
                about dietary fat.
              </p>
              <p>
                Many people find that eating animal foods fits well with their lifestyle and goals.
                As always, work with your healthcare provider to monitor your individual markers.
              </p>
            </div>

            <div className="science-section">
              <h3>Nutrient Density</h3>
              <p>
                Animal foods are among the most nutrient-dense foods available. They contain
                complete proteins with all essential amino acids, fat-soluble vitamins (A, D, E, K),
                and minerals like iron, zinc, and B12 in highly bioavailable forms.
              </p>
              <p>
                Many people appreciate the nutritional simplicity of carnivore—getting a wide range
                of nutrients from whole animal foods without needing to carefully plan and track.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Fasting Connection */}
      <section className="section section-light">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-center mb-xl">Why Carnivore Makes Fasting Easy</h2>
            <p className="science-body-text">
              One of the most remarkable discoveries people make when combining carnivore eating with fasting
              is how naturally they complement each other. Here's why:
            </p>

            <div className="grid-stack-lg mt-xl">
              <div className="science-fasting-card">
                <h4 className="h4-accent-mb">Stable Blood Sugar</h4>
                <p className="no-margin color-text-light">
                  Without carbohydrates causing blood sugar spikes and crashes, your energy stays constant.
                  The desperate hunger that drives snacking simply disappears.
                </p>
              </div>

              <div className="science-fasting-card">
                <h4 className="h4-accent-mb">Already Fat-Adapted</h4>
                <p className="no-margin color-text-light">
                  Your body is already burning fat for fuel. When you fast, it simply continues using fat—
                  either from your food or from your body stores. No transition period required.
                </p>
              </div>

              <div className="science-fasting-card">
                <h4 className="h4-accent-mb">True Satiation</h4>
                <p className="no-margin color-text-light">
                  Protein and fat trigger powerful satiety hormones. You eat until genuinely satisfied,
                  which makes extending your fasting window feel natural rather than forced.
                </p>
              </div>

              <div className="science-fasting-card">
                <h4 className="h4-accent-mb">Mental Clarity Amplified</h4>
                <p className="no-margin color-text-light">
                  Both carnivore and fasting enhance mental clarity through ketone production.
                  Combined, they create a state of focus and clarity that many describe as life-changing.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="section">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-center mb-xl">Common Questions About Carnivore</h2>

            <div className="science-question-block">
              <h4 className="h4-accent">How do people get enough nutrients?</h4>
              <p>
                Animal foods are incredibly nutrient-dense. They contain complete proteins, essential fatty acids,
                and highly bioavailable vitamins and minerals. Many people find they feel well-nourished eating
                this way, though we always recommend working with your doctor and monitoring your health.
              </p>
            </div>

            <div className="science-question-block">
              <h4 className="h4-accent">What about variety?</h4>
              <p>
                While it might seem limited, there's actually a lot of variety within animal foods: beef, pork,
                lamb, poultry, fish, eggs, and organ meats. Many people find the simplicity refreshing—no more
                decision fatigue about what to eat.
              </p>
            </div>

            <div className="science-question-block">
              <h4 className="h4-accent">Is it sustainable long-term?</h4>
              <p>
                Many people have eaten this way for years. The simplicity is often what makes it sustainable—
                there's nothing to track, count, or measure. You eat when you're hungry, stop when you're full,
                and move on with your day.
              </p>
            </div>

            <div className="science-question-block">
              <h4 className="h4-accent">What about fasting—isn't that hard?</h4>
              <p>
                Many people find fasting much easier on a carnivore diet because they're already fat-adapted.
                Without blood sugar spikes and crashes, hunger becomes manageable and predictable rather than
                urgent and demanding.
              </p>
            </div>

            <div>
              <h4 className="h4-accent">Should I talk to my doctor?</h4>
              <p>
                Yes, absolutely. We always recommend consulting with your healthcare provider before making
                significant dietary changes, especially if you have existing health conditions or take medications.
                This is educational information, not medical advice.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What the Research Shows */}
      <section className="section section-light">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-center mb-xl">What the Research Shows</h2>
            <p className="text-center mb-xl color-text-light">
              While large-scale carnivore diet studies are still emerging, the individual components have extensive research:
            </p>

            <div className="grid-2 gap-lg">
              <div className="science-research-card">
                <h4 className="h4-mb">On Ketogenic Diets</h4>
                <ul className="science-research-list">
                  <li>Used therapeutically since the 1920s</li>
                  <li>Well-studied metabolic effects</li>
                  <li>Shifts body to fat-burning mode</li>
                  <li>Many people report mental clarity</li>
                </ul>
              </div>

              <div className="science-research-card">
                <h4 className="h4-mb">On Intermittent Fasting</h4>
                <ul className="science-research-list">
                  <li>Increases autophagy (cellular recycling)</li>
                  <li>Simplifies eating schedule</li>
                  <li>Widely practiced throughout history</li>
                  <li>Easy to combine with carnivore</li>
                </ul>
              </div>

              <div className="science-research-card">
                <h4 className="h4-mb">On Elimination Diets</h4>
                <ul className="science-research-list">
                  <li>Help identify which foods work for you</li>
                  <li>Simplify food choices</li>
                  <li>Remove variables from your diet</li>
                  <li>Create a clear baseline</li>
                </ul>
              </div>

              <div className="science-research-card">
                <h4 className="h4-mb">On Protein & Satiety</h4>
                <ul className="science-research-list">
                  <li>Most satiating macronutrient</li>
                  <li>Supports muscle maintenance</li>
                  <li>Higher thermic effect than carbs or fat</li>
                  <li>Essential for body function</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Your Body Knows */}
      <section className="section">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="mb-lg">Your Body Knows What To Do</h2>
            <p className="science-body-text">
              For millions of years, humans ate what they could hunt and gather—primarily animal foods.
              Our bodies evolved on this diet. The agricultural revolution happened only 10,000 years ago.
              The processed food revolution? Less than 100 years.
            </p>
            <p className="science-body-text">
              Your body isn't broken. It's not confused. It knows exactly how to thrive when given the
              foods it evolved to eat. The science simply confirms what your ancestors knew instinctively.
            </p>
            <p className="science-closing-text">
              The question isn't whether this works. The question is whether you're ready to try it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="section section-light">
        <div className="container-narrow">
          <div className="text-center">
            <h2>A Note on Research</h2>
            <p>
              The carnivore diet is still relatively new to scientific research, though its components
              (meat-based eating, fasting) have been studied extensively. The stories in this book
              represent real experiences, but everyone's body is different.
            </p>
            <p>
              We always recommend working with healthcare professionals, especially if you have
              existing health conditions or take medications. This information is educational, not
              medical advice.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <h2>You Found Your Tribe</h2>
          <p>
            Your people are here. Thousands who are on the same journey, ready to support you.
          </p>
          <Link to="/join" className="btn btn-secondary btn-lg">
            Join the Community
          </Link>
          <p className="cta-footer-text">
            $97 one-time · Lifetime access
          </p>
        </div>
      </section>
    </>
  );
}
